import { type BettingInsightsConfigFactoryOptions, type EsIndex } from '~/apps/product-plus/configuration/types';
import { createBettingSplitsUIConfig } from '~/apps/product-plus/pages/betting-splits/createBettingSplitsUIConfig';
import { createEventOverviewUIConfig } from '~/apps/product-plus/pages/event-overview/createEventOverviewUIConfig';
import { createMatchupCentreUIConfig } from '~/apps/product-plus/pages/match-centre/createMatchupCentreUIConfig';
import { createPlayerDataUIConfig } from '~/apps/product-plus/pages/player/createPlayerDataUIConfig';
import { createPositiveEvUIConfig } from '~/apps/product-plus/pages/positive-ev/route-config/createPositiveEvUIConfig';
import { createPremiumMarketMoversUIConfig } from '~/apps/product-plus/pages/premium-market-movers/createPremiumMarketMoversUIConfig';
import { createTrendsUIConfig } from '~/apps/product-plus/pages/trends/createTrendsUIConfig';
import {
  bettingSplitsIndex,
  eventOverviewIndex,
  matchupCentreIndex,
  playerDataIndex,
  positiveEvIndex,
  premiumMarketMoversIndex,
  type SearchDomainIndexName,
  trendsIndex,
} from '~/apps/product-plus/search-domain/indexes';
import { createBettingInsightsBaseConfig } from '~/apps/product-plus/utils/createBettingInsightsBaseConfig';
import { type RequestContext } from '~/types/RequestContext';

type CreateUiConfig<TUiConfig extends object> = (
  requestContext: RequestContext,
  options: BettingInsightsConfigFactoryOptions,
  tags: string[],
) => TUiConfig;

function createBettingInsightsConfig<TIndex extends SearchDomainIndexName, TUiConfig extends object>(
  indexName: TIndex,
  requestContext: RequestContext,
  options: BettingInsightsConfigFactoryOptions | undefined,
  createUiConfig: CreateUiConfig<TUiConfig>,
) {
  const resolvedOptions = options ?? {};
  const baseConfig = createBettingInsightsBaseConfig(indexName, requestContext, resolvedOptions);
  const uiConfig = createUiConfig(requestContext, resolvedOptions, baseConfig.tags);

  return { ...baseConfig, ...uiConfig };
}

function createConfigFactory<TIndex extends SearchDomainIndexName, TUiConfig extends object>(
  indexName: TIndex,
  createUiConfig: CreateUiConfig<TUiConfig>,
) {
  return function <_TIndex extends EsIndex = TIndex>(
    requestContext: RequestContext,
    options: BettingInsightsConfigFactoryOptions = {},
  ) {
    return createBettingInsightsConfig(indexName, requestContext, options, createUiConfig);
  };
}

export const createBettingSplitsConfig = createConfigFactory(bettingSplitsIndex, createBettingSplitsUIConfig);
export const createEventOverviewConfig = createConfigFactory(eventOverviewIndex, createEventOverviewUIConfig);
export const createMatchupCentreConfig = createConfigFactory(matchupCentreIndex, createMatchupCentreUIConfig);
export const createPlayerDataConfig = createConfigFactory(playerDataIndex, createPlayerDataUIConfig);
export const createPositiveEvConfig = createConfigFactory(positiveEvIndex, createPositiveEvUIConfig);
export const createPremiumMarketMoversConfig = createConfigFactory(
  premiumMarketMoversIndex,
  createPremiumMarketMoversUIConfig,
);
export const createTrendsConfig = createConfigFactory(trendsIndex, createTrendsUIConfig);
